import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatNumber } from '@/lib/format';
import { Building2, Calendar, TrendingUp, Users } from 'lucide-react';

interface ListingCardProps {
  listing: {
    id: string;
    portfolio_name: string;
    seller_org: string;
    asking_price: number;
    account_count: number;
    nominal_value: number;
    irr_target?: number;
    listed_at: string;
    status: 'active' | 'pending' | 'sold';
  };
  onViewDetails: () => void;
}

export function ListingCard({ listing, onViewDetails }: ListingCardProps) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">{listing.portfolio_name}</CardTitle>
            <CardDescription className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {listing.seller_org}
            </CardDescription>
          </div>
          <Badge variant={listing.status === 'active' ? 'default' : 'secondary'}>
            {listing.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground mb-1">Asking Price</div>
            <div className="text-xl font-bold text-primary">
              {formatCurrency(listing.asking_price)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground mb-1">Nominal Value</div>
            <div className="text-lg font-semibold">
              {formatCurrency(listing.nominal_value)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{formatNumber(listing.account_count)} accounts</span>
          </div>
          {listing.irr_target && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              <span>{listing.irr_target}% IRR</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          Listed {new Date(listing.listed_at).toLocaleDateString()}
        </div>

        <Button onClick={onViewDetails} className="w-full">
          View Details & Make Offer
        </Button>
      </CardContent>
    </Card>
  );
}
